import { Drone } from '../types/drone';

/**
 * When a drone's credentials need its pilot (#777, C16): from 7 days before
 * they expire, and once they no longer work.
 */
export const CREDENTIALS_WARNING_MS = 7 * 24 * 60 * 60 * 1000;

export type CredentialsAttention = 'expiring' | 'expired' | 'revoked';

/** Whether the drone's credentials need renewing, or soon will; null if not. */
export const credentialsAttention = (drone: Drone, now: number): CredentialsAttention | null => {
  const c = drone.credentials;
  if (!c) return null;
  if (c.state !== 'valid') return c.state;
  const expiresAt = Date.parse(c.expiresAt);
  if (expiresAt <= now) return 'expired';
  return expiresAt - now <= CREDENTIALS_WARNING_MS ? 'expiring' : null;
};

/**
 * The command that renews the drone (#777, C3, C14). The browser never holds
 * a token: renewal runs from a terminal that can reach the drone. A virtual
 * drone runs in the cluster; an enrolled drone is usually an aircraft, but the
 * cluster simulator is enrolled too, so both commands are named for it.
 */
export const renewCommands = (drone: Drone): string[] =>
  drone.virtual
    ? [`fleetctl renew -k8s -drone-id ${drone.id}`]
    : [`fleetctl renew -ssh USER@HOST -drone-id ${drone.id}`, `fleetctl renew -k8s -drone-id ${drone.id}`];

const DAY_MS = 24 * 60 * 60 * 1000;

/** A short label for the attention, as the chip shows it. */
export const credentialsAttentionLabel = (drone: Drone, attention: CredentialsAttention, now: number): string => {
  switch (attention) {
    case 'revoked':
      return 'CREDENTIALS REVOKED';
    case 'expired':
      return 'CREDENTIALS EXPIRED';
    case 'expiring': {
      const days = Math.max(1, Math.ceil((Date.parse(drone.credentials?.expiresAt ?? '') - now) / DAY_MS));
      return `CREDENTIALS EXPIRE IN ${days} ${days === 1 ? 'DAY' : 'DAYS'}`;
    }
  }
};
